import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ENV_CONFIG, EnvironmentConfig, NFIdentity } from '@nibras/core/common';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, delay, tap } from 'rxjs/operators';
import { AppGlobalService } from '../app-global.service';
import { HttpService } from '../http.service';
import { AppConstants } from '../model/constants.model';
import { dcTag } from '../model/dcTag.model';
import { Token, User } from '../model/user.model';
import { ServiceBaseService } from '../service-base.service';
import { SharedCommonService } from '../shared-common.service';
import { userLogoutAction } from '../store/core-store.actions';

const CHECK_INTERVAL = 15000; // in ms

@Injectable()
export class AuthService extends ServiceBaseService {
  private readonly apiRoot = 'Auth/';
  private readonly endPoints = {
    GET_TOKEN: `${this.apiRoot}AuthenticateUser`,
    REFRESH_NXTOKEN: `${this.apiRoot}RefreshNXToken`
  };
  private readonly localStorageKeys = {
    USER_SESSION_ID: 'userSessionID',
    IS_USER_LOGGED_IN: 'isUserLoggedin',
    ACCESS_TOKEN: 'access_token',
    USERNAME: 'username'
  };

  isLoggedIn = false;
  userId = '';
  userFullName: string;
  userElementVersionId = 0;
  //  isProfilingEnabled = false;
  NXToken = '';
  NXTokenExpiry: Date;
  UserSecurityTag: dcTag;
  private _userSessionID: string;
  private timer: any = null;
  set userSessionID(value: string) {
    this._userSessionID = value;
    localStorage.setItem(this.localStorageKeys.USER_SESSION_ID, value);
  }
  get userSessionID() {
    if (this._userSessionID) return this._userSessionID;
    if (localStorage.getItem(this.localStorageKeys.USER_SESSION_ID) != '') {
      return (this.userSessionID = localStorage.getItem(this.localStorageKeys.USER_SESSION_ID));
    }
    //localStorage.setItem(this.localStorageKeys.USER_SESSION_ID, value)
  }
  // store the URL so we can redirect after logging in
  redirectUrl: string;
  //To compare the user session expired or not
  private futureDate: Date;
  private isRefreshTokenTriggered: boolean;

  constructor(
    private store: Store,
    public router: Router,
    private httpService: HttpService,
    @Inject(ENV_CONFIG) private environmentConfig: EnvironmentConfig
  ) {
    super();

    if (localStorage.getItem(this.localStorageKeys.IS_USER_LOGGED_IN) == 'true') {
      this.isLoggedIn = true;
    }
    if (this.isLoggedIn) {
      if (localStorage.getItem(this.localStorageKeys.USERNAME) != '') {
        this.userId = localStorage.getItem(this.localStorageKeys.USERNAME);
      }
    }
  }

  login(user: User): Observable<boolean> {
    localStorage.setItem(this.localStorageKeys.USERNAME, user.LoginID);
    this.userId = user.LoginID;
    this.userElementVersionId = user.UserSecurityID;
    this.NXToken = user.NXToken;
    this.NXTokenExpiry = new Date(user.NXTokenExpiry);
    //this.isProfilingEnabled = user.IsProfilingEnabled;
    this.userFullName = user.FirstName + ' ' + user.LastName;
    this.UserSecurityTag = user.Tags.find(
      tag =>
        tag.ObjectID == user.UserSecurityID &&
        tag.SourceObjectID == user.UserSecurityID &&
        tag.ElementTypeID == NFIdentity.Security_Identity &&
        tag.CategoryID == +NFIdentity.ElementSelfTag
    );
    this.isRefreshTokenTriggered = false;
    AppGlobalService.loginID = user.LoginID;
    // this.userElementVersionId = userEVId;
    localStorage.setItem(this.localStorageKeys.IS_USER_LOGGED_IN, 'true');
    this.initInterval();
    return observableOf(true).pipe(
      delay(200),
      tap(() => (this.isLoggedIn = true))
    ); //TODO:
  }

  logout(): void {
    this.isLoggedIn = false;
    this.userId = '';
    this.userElementVersionId = 0;
    localStorage.removeItem(this.localStorageKeys.IS_USER_LOGGED_IN);
    localStorage.removeItem(this.localStorageKeys.USERNAME);
    this.store.dispatch(userLogoutAction());
    this.router.navigate(['/login']);
    this.isRefreshTokenTriggered = true;
  }

  signin(): void {
    this.router.navigate(['/']);
  }

  getUser(): Observable<User> {
    const defaultRoleId = +localStorage.getItem(AppConstants.SELECTED_ROLE_ID_KEY);

    return this.httpService
      .get<User>(`${this.endPoints.GET_TOKEN}${defaultRoleId ? '?defaultRoleId=' + defaultRoleId : ''}`)
      .pipe(catchError(this._errorHandler));
  }

  getToken(username: string, password: string): Observable<Token> {
    const loginData = 'grant_type=password&username=' +
      username +
      '&password=' +
      password +
      '&client_id=' +
      this.environmentConfig.client_id;

    return this.httpService.post<Token>('token', loginData).pipe(
      tap(token => {
        localStorage.setItem(this.localStorageKeys.USERNAME, token.userName);
        localStorage.setItem(this.localStorageKeys.IS_USER_LOGGED_IN, 'true');
        localStorage.setItem(this.localStorageKeys.ACCESS_TOKEN, token.access_token);
      }),
      catchError(this._errorHandler)
    );
  }

  getAccessToken(): string {
    return localStorage.getItem(this.localStorageKeys.ACCESS_TOKEN);
  }

  private initInterval() {
    this.stopInterval();
    this.timer = setInterval(() => {
      this.isNXTokenExpired();
    }, CHECK_INTERVAL);
  }

  private stopInterval() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private isNXTokenExpired() {
    const currentDate = new Date();
    //convert current datetime to
    if (currentDate > this.NXTokenExpiry && !this.isRefreshTokenTriggered) {
      this.isRefreshTokenTriggered = true;
      this.stopInterval();
      this.refreshToken();
    }
  }

  private refreshToken() {
    this.httpService.get<User>(`${this.endPoints.REFRESH_NXTOKEN}/${this.NXToken}/`).subscribe({
      next: (data) => {
        if (data) {
          const oldTokenExpiryTime: Date = this.NXTokenExpiry;
          this.NXToken = data.NXToken;
          this.NXTokenExpiry = new Date(data.NXTokenExpiry);
          if (SharedCommonService.compareDate(oldTokenExpiryTime, this.NXTokenExpiry) != 0) {
            this.initInterval();
            this.isRefreshTokenTriggered = false;
          }
        }
      },
      error: (err) => console.log('Error caught at Subscriber ' + err),
      complete: () => console.log('Processing Complete.')
    });
  }
}
